import { Badge } from "@/components/ui/Badge";
import { useInvoices } from "@/contexts/InvoicesContext";
import { STATUS_COLORS, styles } from "@/pages/InvoicesPage.styles";
import * as shared from "@/styles/shared";
import type { Status } from "@shared/types/models";
import { Building2, Calendar as CalendarIcon, Tag, X } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as filtersBarStyles from "./FiltersBar.styles";

const FiltersBar = () => {
  const filters = useInvoices();
  const { t } = useTranslation(["common", "invoices", "status"]);

  const hasActiveFilters =
    !!filters.statuses.length ||
    !!filters.filterProviderIds.length ||
    !!filters.filterAddressIds.length ||
    filters.dateFrom !== "" ||
    filters.dateTo !== "";

  const toggleStatus = useCallback(
    (id: number | string) => {
      const s = id as Status;
      filters.setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    },
    [filters.setStatuses],
  );

  const toggleProvider = useCallback(
    (id: number | string) => {
      const n = Number(id);
      filters.setFilterProviderIds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
    },
    [filters.setFilterProviderIds],
  );

  const toggleAddress = useCallback(
    (id: number | string) => {
      const n = Number(id);
      filters.setFilterAddressIds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
    },
    [filters.setFilterAddressIds],
  );

  const clearAllFilters = useCallback(() => {
    filters.setStatuses([]);
    filters.setFilterProviderIds([]);
    filters.setFilterAddressIds([]);
    filters.setDateFrom("");
    filters.setDateTo("");
  }, [filters.setStatuses, filters.setFilterProviderIds, filters.setFilterAddressIds, filters.setDateFrom, filters.setDateTo]);

  const statusOptions = Object.keys(STATUS_COLORS).map((s) => ({ id: s, name: t(s, { ns: "status" }) }));

  return (
    <div className={styles.filtersBar}>
      {/* Filter chips with icons */}
      <div className={styles.filterGroup}>
        <span className={`${styles.filterLabel} ${filtersBarStyles.filterLabelRow}`}>
          <Tag className={shared.iconXs} />
          {t("filterStatus", { ns: "invoices" })}
        </span>
        <div className={styles.chipWrapper}>
          {statusOptions.map((opt) => {
            const isActive = filters.statuses.includes(opt.id as Status);
            return (
              <Badge
                key={opt.id}
                variant={isActive ? "default" : "secondary"}
                onClick={() => toggleStatus(opt.id)}
                className={filtersBarStyles.cursorPointer}
              >
                {opt.name}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={`${styles.filterLabel} ${filtersBarStyles.filterLabelRow}`}>
          <Building2 className={shared.iconXs} />
          {t("filterProvider", { ns: "invoices" })}
        </span>
        <div className={styles.chipWrapper}>
          {filters.providers.map((opt) => {
            const isActive = filters.filterProviderIds.includes(Number(opt.id));
            return (
              <Badge
                key={opt.id}
                variant={isActive ? "default" : "secondary"}
                onClick={() => toggleProvider(opt.id)}
                className={filtersBarStyles.cursorPointer}
              >
                {opt.name}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={`${styles.filterLabel} ${filtersBarStyles.filterLabelRow}`}>
          <Building2 className={shared.iconXs} />
          {t("filterAddress", { ns: "invoices" })}
        </span>
        <div className={styles.chipWrapper}>
          {filters.addresses.map((opt) => {
            const isActive = filters.filterAddressIds.includes(Number(opt.id));
            return (
              <Badge
                key={opt.id}
                variant={isActive ? "default" : "secondary"}
                onClick={() => toggleAddress(opt.id)}
                className={filtersBarStyles.cursorPointer}
              >
                {opt.name}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={`${styles.filterLabel} ${filtersBarStyles.filterLabelRow}`}>
          <CalendarIcon className={shared.iconXs} />
          {t("filterDate", { ns: "invoices" })}
        </span>
        <div className={filtersBarStyles.dateInputWrapper}>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => filters.setDateFrom(e.target.value)}
            className={styles.filterInput}
            placeholder={t("filters.dateFrom", { ns: "invoices" })}
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => filters.setDateTo(e.target.value)}
            className={styles.filterInput}
            placeholder={t("filters.dateTo", { ns: "invoices" })}
          />
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className={filtersBarStyles.clearAllBtn}>
              <X className={shared.iconXs} />
              {t("clearAll", { ns: "invoices" })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { FiltersBar };
